import { useEffect, useMemo, useState } from "react";
import { ExternalLink } from "lucide-react";
import { ApiError } from "@/api/client";
import { useCreateBackend, useUpdateBackend } from "@/api/backends";
import type { Backend, BackendKind } from "@/api/types";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import {
  BACKEND_KIND_META,
  RAVEN_FIELDS,
  detectActiveGroup,
  type CredentialField,
  type KindMeta,
} from "./credentialSchemas";

const KINDS: BackendKind[] = ["grafana", "azure", "aws", "datadog", "dynatrace", "splunk", "adx", "solarwinds"];

function blankCreds(meta: KindMeta): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of meta.fields) out[f.key] = "";
  return out;
}

function blankRaven(): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of RAVEN_FIELDS) out[f.key] = "";
  return out;
}

function pickRaven(credentials: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = {};
  for (const f of RAVEN_FIELDS) {
    const v = credentials[f.key];
    if (v) out[f.key] = v;
  }
  // Legacy: earlier seeder stored the per-connection ingest_token under
  // `raven_hmac_secret`. If raven_ingest_token is missing but
  // raven_hmac_secret looks like a long hex (not the literal "demosim"),
  // surface it in the ingest_token field so the user can confirm.
  if (
    !out.raven_ingest_token &&
    out.raven_hmac_secret &&
    out.raven_hmac_secret !== "demosim" &&
    out.raven_hmac_secret.length >= 32
  ) {
    out.raven_ingest_token = out.raven_hmac_secret;
    delete out.raven_hmac_secret;
  }
  if (!out.raven_hmac_secret && credentials.raven_signing_secret) {
    out.raven_hmac_secret = credentials.raven_signing_secret;
  }
  return out;
}

export function BackendEditDialog({
  open,
  onOpenChange,
  existing,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  existing: Backend | null;
}) {
  const isCreate = !existing;
  const create = useCreateBackend();
  const update = useUpdateBackend();

  const [name, setName] = useState("");
  const [kind, setKind] = useState<BackendKind>("grafana");
  const [creds, setCreds] = useState<Record<string, string>>({});
  const [raven, setRaven] = useState<Record<string, string>>({});
  const [activeGroup, setActiveGroup] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const meta = BACKEND_KIND_META[kind];

  // Reset state whenever the dialog opens or `existing` changes.
  useEffect(() => {
    if (!open) return;
    const initialKind = existing?.kind ?? "grafana";
    const m = BACKEND_KIND_META[initialKind];
    const existingCreds = existing?.credentials ?? {};
    const initialCreds = { ...blankCreds(m), ...existingCreds };
    setName(existing?.name ?? "");
    setKind(initialKind);
    setCreds(initialCreds);
    setRaven({ ...blankRaven(), ...pickRaven(existingCreds) });
    setActiveGroup(detectActiveGroup(m, initialCreds));
    setError(null);
  }, [existing, open]);

  // When the user changes the kind, switch credential template.
  // Raven fields are common to every kind, so we preserve them.
  function onKindChange(next: BackendKind) {
    const m = BACKEND_KIND_META[next];
    setKind(next);
    setCreds(blankCreds(m));
    setActiveGroup(detectActiveGroup(m, {}));
  }

  function setField(key: string, value: string) {
    setCreds((prev) => ({ ...prev, [key]: value }));
  }

  function setRavenField(key: string, value: string) {
    setRaven((prev) => ({ ...prev, [key]: value }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);

    // Only submit fields the active group cares about, plus any non-grouped.
    const active = activeGroup;
    const payload: Record<string, string> = {};
    for (const f of meta.fields) {
      if (f.group && active && f.group !== active) continue;
      const v = creds[f.key]?.trim();
      if (v) payload[f.key] = v;
    }
    // Raven webhook routing — shared across every backend kind so each
    // backend can fire alerts into its own NeuBird project.
    for (const f of RAVEN_FIELDS) {
      const v = raven[f.key]?.trim();
      if (v) payload[f.key] = v;
    }

    try {
      if (isCreate) {
        await create.mutateAsync({ name, kind, credentials: payload });
      } else {
        await update.mutateAsync({
          ...existing!,
          name,
          kind,
          credentials: payload,
        });
      }
      onOpenChange(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  const visibleFields = useMemo(
    () => meta.fields.filter((f) => !f.group || f.group === activeGroup),
    [meta, activeGroup],
  );

  const pending = create.isPending || update.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-auto" aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>
            {isCreate ? "New backend" : `Edit ${existing!.name}`}
          </DialogTitle>
        </DialogHeader>

        <form className="space-y-5" onSubmit={onSubmit}>
          <div className="grid grid-cols-[1fr_12rem] gap-3">
            <div className="space-y-1.5">
              <label htmlFor="backend-name" className="text-sm font-medium text-foreground">
                Name
              </label>
              <Input
                id="backend-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. demosim-grafana"
                required
              />
            </div>
            <div className="space-y-1.5">
              <label htmlFor="backend-kind" className="text-sm font-medium text-foreground">
                Kind
              </label>
              <Select value={kind} onValueChange={(v) => onKindChange(v as BackendKind)}>
                <SelectTrigger id="backend-kind">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {KINDS.map((k) => (
                    <SelectItem key={k} value={k}>
                      {BACKEND_KIND_META[k].label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            {meta.description}
            {meta.docsUrl && (
              <a
                href={meta.docsUrl}
                target="_blank"
                rel="noreferrer"
                className="ml-2 inline-flex items-center gap-1 text-foreground underline"
              >
                Docs <ExternalLink size={11} />
              </a>
            )}
          </div>

          {meta.groups && activeGroup && (
            <div className="space-y-1">
              <div className="inline-flex rounded-md bg-muted/40 border border-border p-0.5">
                {meta.groups.map((g) => (
                  <button
                    key={g.id}
                    type="button"
                    onClick={() => setActiveGroup(g.id)}
                    aria-pressed={activeGroup === g.id}
                    className={cn(
                      "px-3 h-7 rounded-sm text-xs font-medium transition-colors",
                      activeGroup === g.id
                        ? "bg-card text-foreground shadow-sm"
                        : "text-muted-foreground hover:text-foreground",
                    )}
                  >
                    {g.label}
                  </button>
                ))}
              </div>
              <p className="text-xs text-muted-foreground">
                {meta.groups.find((g) => g.id === activeGroup)?.description}
              </p>
            </div>
          )}

          <div className="space-y-3">
            {visibleFields.map((f) => (
              <CredentialFieldInput
                key={f.key}
                field={f}
                value={creds[f.key] ?? ""}
                onChange={(v) => setField(f.key, v)}
              />
            ))}
          </div>

          <div className="space-y-3 rounded-md border border-border bg-muted/20 px-3 py-3">
            <div>
              <h3 className="text-sm font-medium text-foreground">
                Alert routing (Raven webhook)
              </h3>
              <p className="text-xs text-muted-foreground">
                Scenario alerts and noise streams fire into this NeuBird project. Leave
                blank to fall back to the runner's process-env defaults.
              </p>
            </div>
            {RAVEN_FIELDS.map((f) => (
              <CredentialFieldInput
                key={f.key}
                field={f}
                value={raven[f.key] ?? ""}
                onChange={(v) => setRavenField(f.key, v)}
              />
            ))}
          </div>

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={pending}>
              {pending ? (isCreate ? "Creating…" : "Saving…") : isCreate ? "Create" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function CredentialFieldInput({
  field,
  value,
  onChange,
}: {
  field: CredentialField;
  value: string;
  onChange: (v: string) => void;
}) {
  const inputId = `cred-${field.key}`;
  return (
    <div className="space-y-1">
      <div className="flex items-baseline gap-2 flex-wrap">
        <label htmlFor={inputId} className="text-sm font-medium text-foreground">
          {field.label}
        </label>
        <code className="text-[10px] text-muted-foreground font-mono truncate">{field.key}</code>
      </div>
      {field.multiline ? (
        <Textarea
          id={inputId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          rows={3}
          className={cn("font-mono text-xs", field.secret && "tracking-wider")}
        />
      ) : (
        <Input
          id={inputId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder}
          type={field.secret ? "password" : "text"}
          className="font-mono text-sm"
        />
      )}
      {field.description && (
        <p className="text-xs text-muted-foreground">{field.description}</p>
      )}
    </div>
  );
}
